import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { AuthService } from '../auth/auth.service';

interface RecordedVideoOutput {
  blob: Blob;
  url: string;
  title: string;
}

@Injectable()
export class VideoRecordingService {

  private stream: MediaStream;
  private recorder: MediaRecorder;
  private chunks: Blob[] = [];
  private interval;
  private _stream = new Subject<MediaStream>();
  private _recorded = new Subject<RecordedVideoOutput>();
  private _recordedUrl = new Subject<string>();
  private _recordingFailed = new Subject<string>();

  constructor(private httpClient: HttpClient, private authService: AuthService) { }

  getRecordedUrl(): Observable<string> {
    return this._recordedUrl.asObservable();
  }

  getRecordedBlob(): Observable<RecordedVideoOutput> {
    return this._recorded.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this._recordingFailed.asObservable();
  }

  getStream(): Observable<MediaStream> {
    return this._stream.asObservable();
  }

  startRecording(conf: any): Promise<any> {
    console.log('[VideoRec] startRecording called | conf:', JSON.stringify(conf));
    console.log('[VideoRec] navigator.mediaDevices:', !!navigator.mediaDevices, '| getUserMedia:', !!(navigator.mediaDevices?.getUserMedia));
    console.log('[VideoRec] MediaRecorder available:', typeof MediaRecorder !== 'undefined');
    console.log('[VideoRec] userAgent:', navigator.userAgent);

    if (this.recorder) {
      console.warn('[VideoRec] recorder already exists – returning null');
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia(conf).then(stream => {
        console.log('[VideoRec] getUserMedia SUCCESS | stream.active:', stream.active);
        console.log('[VideoRec] videoTracks:', JSON.stringify(stream.getVideoTracks().map(t => ({ label: t.label, readyState: t.readyState, settings: t.getSettings() }))));
        console.log('[VideoRec] audioTracks:', JSON.stringify(stream.getAudioTracks().map(t => ({ label: t.label, readyState: t.readyState }))));
        this.stream = stream;
        this.record();
        resolve(this.stream);
      }).catch(error => {
        console.error('[VideoRec] getUserMedia FAILED | name:', error?.name, '| message:', error?.message);
        this._recordingFailed.next(null);
        reject();
      });
    });
  }

  abortRecording() {
    this.stopMedia();
  }

  private getSupportedMimeType(): string {
    const types = ['video/mp4', 'video/webm;codecs=h264', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    console.log('[VideoRec] Testing MIME type support:');
    for (const type of types) {
      const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
      console.log(`[VideoRec]   ${type}: ${supported}`);
      if (supported) {
        console.log('[VideoRec] --> Selected MIME type:', type);
        return type;
      }
    }
    console.warn('[VideoRec] --> No MIME type supported, using browser default');
    return '';
  }

  private record() {
    this.chunks = [];
    const mimeType = this.getSupportedMimeType();
    const options: MediaRecorderOptions = mimeType
      ? { mimeType, videoBitsPerSecond: 600000 }
      : { videoBitsPerSecond: 600000 };

    console.log('[VideoRec] record() | mimeType:', mimeType || '(browser default)', '| options:', JSON.stringify(options));

    try {
      this.recorder = new MediaRecorder(this.stream, options);
      console.log('[VideoRec] MediaRecorder created | state:', this.recorder.state, '| actual mimeType:', this.recorder.mimeType);
    } catch (e: any) {
      console.error('[VideoRec] MediaRecorder creation FAILED | name:', e?.name, '| message:', e?.message);
      this._recordingFailed.next(null);
      return;
    }

    this.recorder.onstart = () => {
      console.log('[VideoRec] recorder.onstart | state:', this.recorder?.state);
    };

    this.recorder.ondataavailable = (event: BlobEvent) => {
      const totalSize = this.chunks.reduce((acc, c) => acc + c.size, 0);
      console.log('[VideoRec] ondataavailable | data.size:', event.data?.size, '| chunks so far:', this.chunks.length, '| total accumulated size:', totalSize);
      if (event.data && event.data.size > 0) {
        this.chunks.push(event.data);
      } else {
        console.warn('[VideoRec] ondataavailable: chunk is EMPTY (size=0 or no data)');
      }
    };

    this.recorder.onerror = (event: any) => {
      console.error('[VideoRec] MediaRecorder ERROR | error.name:', event?.error?.name, '| error.message:', event?.error?.message, '| full event:', event);
    };

    this.recorder.onstop = () => {
      const totalSize = this.chunks.reduce((acc, c) => acc + c.size, 0);
      console.log('[VideoRec] onstop fired | chunks:', this.chunks.length, '| total size:', totalSize);
      const blob = new Blob(this.chunks, { type: mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      const recordedName = encodeURIComponent('video_' + new Date().getTime() + '.mp4');
      console.log('[VideoRec] Blob created | blob.size:', blob.size, '| blob.type:', blob.type, '| objectURL:', url);
      this._recorded.next({ blob, url, title: recordedName });
      this.stopMedia();
    };

    this.recorder.start(1000);
    console.log('[VideoRec] recorder.start(1000) called | state:', this.recorder.state);

    this.interval = setInterval(() => {
      this._stream.next(this.stream);
    }, 500);
  }

  stopRecording() {
    console.log('[VideoRec] stopRecording called | recorder:', this.recorder ? `state=${this.recorder.state}` : 'null');
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.stop();
      console.log('[VideoRec] recorder.stop() called | new state:', this.recorder.state);
    } else {
      console.warn('[VideoRec] stopRecording: nothing to stop – recorder is', this.recorder ? this.recorder.state : 'null');
    }
  }

  private stopMedia() {
    this.recorder = null;
    clearInterval(this.interval);
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  public async createVideoRecordingHistoryEntry(request: any): Promise<any> {
    const url = '/api/recordinghistory/create/' + request.packageId;
    return await firstValueFrom(this.httpClient.post(url, request));
  }
}
